import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const SLEEPERCDN = 'https://sleepercdn.com/content/nfl/players/thumb/';

// Counts up to `target` on mount — the number climbing reads as more of a win than it just appearing.
function useCountUp(target, duration = 900) {
  const [val, setVal] = useState(0);
  const reduceMotion = useRef(typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
  useEffect(() => {
    if (target == null) { setVal(0); return; }
    if (reduceMotion.current) { setVal(target); return; }
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
}

// A small brand/green confetti burst for the biggest win moments (card full, rating unlocked).
function Confetti() {
  return (
    <div className="are-confetti">
      {Array.from({ length: 8 }).map((_, i) => (
        <i key={i} style={{ left: `${8 + i * 11}%`, animationDelay: `${i * 70}ms` }} />
      ))}
    </div>
  );
}

const styles = `
  .are-page {
    min-height: calc(100vh - 60px);
    background: #FFFFFF;
    font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif;
    color: #1D1D1F;
    --ts-paper: #FFFDF8;
    --ts-paper-edge: #F0E7D8;
    --ts-mono: 'SF Mono', ui-monospace, 'Menlo', 'Roboto Mono', monospace;
    --ft-brand: #FF7A00;
    --ft-brand-soft: rgba(255,122,0,.13);
    --ft-brand-ink: #C25E00;
    --ft-green: #34C759;
    --ft-green-soft: rgba(52,199,89,.14);
    --ft-green-ink: #1B873F;
    --ft-ease: cubic-bezier(0.16,1,0.3,1);
  }

  /* ── Awning (signature motif) ── */
  .are-awning {
    height: 14px; position: relative;
    background: repeating-linear-gradient(90deg, var(--ft-brand) 0 34px, #FFE7CC 34px 68px);
  }
  .are-awning::after {
    content: ''; position: absolute; left: 0; right: 0; bottom: -11px; height: 12px;
    background: radial-gradient(circle 11px at 17px 0, var(--ft-brand) 0 10px, transparent 11px) 0 0/34px 12px repeat-x;
  }
  .are-awning.green {
    background: repeating-linear-gradient(90deg, var(--ft-green) 0 34px, #D6F4DE 34px 68px);
  }
  .are-awning.green::after {
    background: radial-gradient(circle 11px at 17px 0, var(--ft-green) 0 10px, transparent 11px) 0 0/34px 12px repeat-x;
  }
  .are-awning.muted {
    background: repeating-linear-gradient(90deg, #D2D2D7 0 34px, #EBEBED 34px 68px);
  }
  .are-awning.muted::after {
    background: radial-gradient(circle 11px at 17px 0, #D2D2D7 0 10px, transparent 11px) 0 0/34px 12px repeat-x;
  }

  /* ── Hero ── */
  .are-hero {
    position: relative; overflow: hidden; border-bottom: 1px solid #EBEBED;
    background: linear-gradient(180deg, #F2FBF4, #fff 78%);
  }
  .are-hero.muted { background: linear-gradient(180deg, #F7F7F9, #fff 78%); }
  .are-hero-inner { max-width: 760px; margin: 0 auto; padding: 46px 24px 40px; }
  .are-eyebrow {
    display: inline-flex; align-items: center; gap: 9px;
    font-size: 11px; font-weight: 700; letter-spacing: 0.16em; text-transform: uppercase;
    color: var(--ft-green-ink); margin-bottom: 18px;
  }
  .are-eyebrow.muted { color: #86868B; }
  .are-eyebrow .line { width: 22px; height: 1.5px; background: var(--ft-green); opacity: .5; }
  .are-eyebrow.muted .line { background: #D2D2D7; }
  .are-headline {
    font-size: 60px; font-weight: 700; letter-spacing: -.045em; line-height: .98; margin-bottom: 18px;
  }
  .are-headline .quiet { color: #86868B; }
  .are-dek { font-size: 18px; color: #6E6E73; line-height: 1.5; max-width: 520px; margin-bottom: 4px; }
  .are-dek b { color: #1D1D1F; font-weight: 600; }

  /* ── Content ── */
  .are-content { max-width: 760px; margin: 0 auto; padding: 30px 24px 80px; }
  .are-section { margin-bottom: 38px; }
  .are-sectionhead { display: flex; align-items: center; gap: 10px; margin-bottom: 14px; }
  .are-klabel {
    font-size: 11px; font-weight: 600; letter-spacing: .1em; text-transform: uppercase; color: #86868B;
  }
  .are-opensign {
    display: inline-flex; align-items: center; gap: 8px;
    font-size: 11px; font-weight: 700; letter-spacing: .12em; text-transform: uppercase;
    color: var(--ft-green-ink); background: var(--ft-green-soft); padding: 6px 12px; border-radius: 980px;
  }
  .are-opensign .pip { width: 7px; height: 7px; border-radius: 50%; background: var(--ft-green); animation: are-pip 2.2s infinite; }
  @keyframes are-pip { 0%,100% { opacity: 1; } 50% { opacity: .35; } }
  .are-opensign.brand { color: var(--ft-brand-ink); background: var(--ft-brand-soft); }
  .are-opensign.brand .pip { background: var(--ft-brand); }

  /* ── The finalized RECEIPT ── */
  .are-receiptwrap { display: flex; justify-content: center; }
  .are-receipt {
    width: 100%; max-width: 640px; background: var(--ts-paper);
    border: 1px solid var(--ts-paper-edge); border-radius: 16px; overflow: hidden; position: relative;
    box-shadow: 0 1px 0 var(--ts-paper-edge), 0 18px 44px rgba(120,80,20,.10);
    animation: are-rcup .7s var(--ft-ease) both;
  }
  @keyframes are-rcup { from { opacity: 0; transform: translateY(18px) scale(.985); } to { opacity: 1; transform: none; } }
  .are-receipt::before {
    content: ''; position: absolute; left: 0; right: 0; top: -7px; height: 14px;
    background: radial-gradient(circle 7px at 7px 14px, transparent 6px, var(--ts-paper) 7px) 0 0/22px 14px repeat-x;
  }
  .are-receipt-top { display: flex; align-items: center; gap: 8px; padding: 14px 18px; border-bottom: 1px dashed var(--ts-paper-edge); }
  .are-receipt-num { font-family: var(--ts-mono); font-size: 12.5px; color: #6E6E73; }
  .are-receipt-num b { color: #1D1D1F; }
  .are-receipt-served {
    margin-left: auto; display: inline-flex; align-items: center; gap: 6px;
    font-size: 11px; font-weight: 700; letter-spacing: .1em; text-transform: uppercase; color: var(--ft-green-ink);
  }
  .are-receipt-served .dot { width: 7px; height: 7px; border-radius: 50%; background: var(--ft-green); }
  .are-servedstamp {
    position: absolute; top: 78px; right: 26px; z-index: 3; font-family: var(--ts-mono);
    font-size: 17px; font-weight: 700; letter-spacing: .14em; color: var(--ft-green);
    border: 2.5px solid var(--ft-green); border-radius: 8px; padding: 6px 13px;
    transform: rotate(-11deg); opacity: .85; pointer-events: none; box-shadow: 0 0 0 3px rgba(52,199,89,.08);
  }
  .are-ticket-grid { display: flex; align-items: stretch; position: relative; }
  .are-tcol { flex: 1; min-width: 0; padding: 16px 18px; }
  .are-tcol.get { background: rgba(52,199,89,.05); }
  .are-tcol-k {
    font-size: 10px; font-weight: 700; letter-spacing: .09em; text-transform: uppercase;
    margin-bottom: 12px; display: flex; align-items: center; gap: 7px;
  }
  .are-tcol.get .are-tcol-k { color: var(--ft-green-ink); }
  .are-tcol.give .are-tcol-k { color: #6E6E73; }
  .are-tdiv {
    flex: none; width: 42px; display: flex; flex-direction: column; align-items: center; justify-content: center;
    border-left: 1px dashed var(--ts-paper-edge); border-right: 1px dashed var(--ts-paper-edge); color: #86868B;
  }
  .are-tdiv .for {
    font-size: 10px; font-weight: 700; letter-spacing: .06em; color: #86868B;
    writing-mode: vertical-rl; transform: rotate(180deg);
  }
  .are-titem {
    display: flex; align-items: center; gap: 9px; background: #fff;
    border: 1px solid var(--ts-paper-edge); border-radius: 10px; padding: 8px 10px; margin-bottom: 7px;
  }
  .are-titem:last-child { margin-bottom: 0; }
  .are-empty { font-size: 12px; color: #86868B; font-style: italic; }
  .are-av {
    width: 32px; height: 32px; border-radius: 50%; background: #D2D2D7; flex: none; overflow: hidden;
    display: flex; align-items: center; justify-content: center; font-size: 10px; font-weight: 700; color: #fff;
  }
  .are-av img { width: 100%; height: 100%; object-fit: cover; }
  .are-pick-av {
    width: 32px; height: 32px; border-radius: 8px; background: #EBEBED; flex: none;
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    color: #86868B; font-family: var(--ts-mono); font-size: 9px; line-height: 1.1; text-align: center;
  }
  .are-ti-name { font-size: 13px; font-weight: 600; line-height: 1.2; }
  .are-ti-meta { font-size: 10px; color: #86868B; margin-top: 2px; }
  .are-receipt-foot {
    display: flex; align-items: flex-start; gap: 11px; padding: 13px 18px;
    border-top: 1px dashed var(--ts-paper-edge); background: #fff;
  }
  .are-rf-taco { font-size: 18px; flex: none; line-height: 1.4; }
  .are-rf-read { font-size: 12.5px; color: #6E6E73; line-height: 1.45; }
  .are-rf-read .verdict { font-weight: 700; color: var(--ft-green-ink); }
  .are-receipt-tape {
    display: flex; align-items: center; justify-content: space-between; padding: 10px 18px;
    border-top: 1px dashed var(--ts-paper-edge); font-family: var(--ts-mono); font-size: 11px;
    color: #86868B; background: var(--ts-paper);
  }
  .are-receipt-tape .barcode { display: flex; gap: 2px; align-items: flex-end; height: 20px; opacity: .7; }
  .are-receipt-tape .barcode i { width: 2px; background: #1D1D1F; height: 100%; }

  /* ── THE STREAK / RATING UNLOCK MOMENT ── */
  .are-peak { display: grid; grid-template-columns: 1.15fr 1fr; gap: 18px; }
  @media (max-width: 720px) { .are-peak { grid-template-columns: 1fr; } }

  /* punch card */
  .are-punch {
    background: var(--ts-paper); border: 1px solid var(--ts-paper-edge); border-radius: 18px;
    padding: 22px 24px; position: relative; overflow: hidden;
  }
  .are-punch::before {
    content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 6px;
    background: repeating-linear-gradient(180deg, var(--ft-brand) 0 12px, #FFE7CC 12px 24px);
  }
  .are-punch-top { display: flex; align-items: flex-end; justify-content: space-between; gap: 20px; margin-bottom: 18px; padding-left: 8px; }
  .are-punch-title { font-size: 19px; font-weight: 600; letter-spacing: -.01em; }
  .are-punch-sub { font-size: 13px; color: #86868B; margin-top: 3px; }
  .are-streakwrap { text-align: right; flex: none; }
  .are-streaknum { font-size: 34px; font-weight: 700; letter-spacing: -.03em; display: flex; align-items: center; gap: 7px; justify-content: flex-end; }
  .are-streaknum .flame { font-size: 26px; }
  .are-streaklbl { font-size: 11px; color: #86868B; letter-spacing: .04em; text-transform: uppercase; }
  .are-stamps { display: flex; gap: 10px; padding-left: 8px; margin-bottom: 8px; flex-wrap: wrap; }
  .are-stamp {
    width: 46px; height: 46px; border-radius: 50%; border: 2px dashed var(--ts-paper-edge);
    display: flex; align-items: center; justify-content: center; font-size: 22px; color: #D2D2D7; position: relative;
  }
  .are-stamp.done { border-style: solid; border-color: var(--ft-brand); background: var(--ft-brand-soft); color: var(--ft-brand); }
  .are-stamp.fresh {
    border-style: solid; border-color: var(--ft-green); background: var(--ft-green-soft); color: var(--ft-green-ink);
    animation: are-punchin .55s var(--ft-ease) .25s both;
  }
  .are-stamp.fresh::after {
    content: 'NEW'; position: absolute; top: -9px; right: -12px; font-size: 8px; font-weight: 800; letter-spacing: .06em;
    color: #fff; background: var(--ft-green); padding: 2px 5px; border-radius: 980px;
  }
  @keyframes are-punchin {
    0% { transform: scale(2.4) rotate(-14deg); opacity: 0; }
    55% { transform: scale(.9) rotate(2deg); opacity: 1; }
    100% { transform: scale(1) rotate(0); }
  }
  .are-stamp.goal { border-style: solid; border-color: var(--ft-brand); background: #fff; color: var(--ft-brand-ink); font-size: 13px; font-weight: 700; }
  .are-stamp.goal.hit { background: var(--ft-brand); color: #fff; border-color: var(--ft-brand); box-shadow: 0 0 0 4px var(--ft-brand-soft); }
  .are-punch-foot { padding-left: 8px; font-size: 13px; color: #6E6E73; margin-top: 10px; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  .are-punch-foot b { color: var(--ft-brand-ink); }
  .are-cardfull {
    display: inline-flex; align-items: center; gap: 6px; background: var(--ft-brand); color: #fff;
    border-radius: 980px; padding: 5px 12px; font-size: 12px; font-weight: 700; letter-spacing: .02em;
  }

  /* confetti — celebrates the punch card filling and the rating unlock */
  .are-confetti { position: absolute; inset: 0; overflow: hidden; pointer-events: none; }
  .are-confetti i {
    position: absolute; top: -10px; width: 6px; height: 14px; border-radius: 2px; opacity: 0;
    background: var(--ft-brand); animation: are-confetti 1.6s var(--ft-ease) both;
  }
  .are-confetti i:nth-child(odd) { background: var(--ft-green); }
  @keyframes are-confetti {
    0% { opacity: 0; transform: translateY(0) rotate(0deg); }
    12% { opacity: 1; }
    100% { opacity: 0; transform: translateY(150px) rotate(340deg); }
  }

  /* RATING UNLOCK card */
  .are-unlock {
    position: relative; background: #1D1D1F; color: #fff; border-radius: 18px; padding: 24px; overflow: hidden;
    animation: are-rcup .7s var(--ft-ease) .15s both;
  }
  .are-unlock .awn { height: 8px; margin: -24px -24px 18px; background: repeating-linear-gradient(90deg, var(--ft-brand) 0 22px, #FFE7CC 22px 44px); }
  .are-unlock .shine {
    position: absolute; top: 0; bottom: 0; left: -60%; width: 42%;
    background: linear-gradient(105deg, transparent, rgba(255,255,255,.13), transparent);
    transform: skewX(-18deg); animation: are-shine 3.4s var(--ft-ease) 1s infinite;
  }
  @keyframes are-shine { 0% { left: -60%; } 40%,100% { left: 130%; } }
  .are-unlock-tag {
    display: inline-flex; align-items: center; gap: 6px; font-size: 10px; font-weight: 800; letter-spacing: .12em;
    text-transform: uppercase; color: var(--ft-brand); background: rgba(255,122,0,.16); padding: 5px 11px;
    border-radius: 980px; margin-bottom: 14px;
  }
  .are-unlock-tag .new { background: var(--ft-brand); color: #fff; border-radius: 980px; padding: 1px 6px; font-size: 9px; }
  .are-unlock-badge { display: flex; align-items: center; gap: 13px; margin-bottom: 18px; }
  .are-ub-medal {
    width: 54px; height: 54px; border-radius: 14px; background: radial-gradient(circle at 50% 35%, #FFD9A8, #FF9D3D);
    display: flex; align-items: center; justify-content: center; font-size: 28px; flex: none; box-shadow: 0 6px 18px rgba(255,122,0,.35);
  }
  .are-ub-txt .ub-k { font-size: 11px; color: rgba(255,255,255,.55); letter-spacing: .04em; }
  .are-ub-txt .ub-name { font-size: 18px; font-weight: 700; letter-spacing: -.01em; }
  .are-ratingreveal { border-top: 1px solid rgba(255,255,255,.12); padding-top: 16px; }
  .are-rr-k {
    font-size: 11px; font-weight: 600; letter-spacing: .1em; text-transform: uppercase;
    color: rgba(255,255,255,.5); margin-bottom: 6px; display: flex; align-items: center; gap: 8px;
  }
  .are-rr-k .wasloc {
    font-size: 10px; font-weight: 600; letter-spacing: .02em; text-transform: none; color: var(--ft-brand);
    background: rgba(255,122,0,.16); padding: 2px 8px; border-radius: 980px;
  }
  .are-rr-num {
    font-size: 46px; font-weight: 700; letter-spacing: -.03em; line-height: 1; display: flex; align-items: baseline; gap: 10px;
    animation: are-countpop .6s var(--ft-ease) .5s both;
  }
  @keyframes are-countpop { 0% { opacity: 0; transform: translateY(8px) scale(.96); } 100% { opacity: 1; transform: none; } }
  .are-rr-num .rank { font-size: 14px; font-weight: 600; color: rgba(255,255,255,.55); letter-spacing: 0; }
  .are-rr-sub { font-size: 12.5px; color: rgba(255,255,255,.55); margin-top: 9px; line-height: 1.45; }
  .are-rr-sub b { color: #fff; font-weight: 600; }

  /* locked-rating progress (not yet unlocked) */
  .are-locknote {
    background: var(--ts-paper); border: 1px solid var(--ts-paper-edge); border-radius: 18px;
    padding: 20px 22px; display: flex; align-items: center; gap: 14px;
  }
  .are-lock-medal {
    width: 46px; height: 46px; border-radius: 12px; background: #EBEBED; flex: none;
    display: flex; align-items: center; justify-content: center; font-size: 20px; color: #86868B;
  }
  .are-lock-txt .lk { font-size: 14px; font-weight: 600; color: #1D1D1F; }
  .are-lock-txt .lk b { color: var(--ft-brand-ink); }
  .are-lock-txt .lk-sub { font-size: 12.5px; color: #86868B; margin-top: 3px; line-height: 1.4; }

  /* already-unlocked rating (no fresh reveal) */
  .are-rating-plain {
    background: var(--ts-paper); border: 1px solid var(--ts-paper-edge); border-radius: 18px;
    padding: 20px 22px; display: flex; align-items: baseline; justify-content: space-between; flex-wrap: wrap; gap: 8px;
  }
  .are-rating-plain .rp-k { font-size: 11px; font-weight: 600; letter-spacing: .1em; text-transform: uppercase; color: #86868B; }
  .are-rating-plain .rp-num { font-size: 30px; font-weight: 700; letter-spacing: -.02em; color: #1D1D1F; }
  .are-rating-plain .rp-rank { font-size: 13px; color: #86868B; font-weight: 500; }

  /* ── Rate your trading partner ── */
  .are-ratecard {
    background: var(--ts-paper); border: 1px solid var(--ts-paper-edge); border-radius: 18px; padding: 24px;
  }
  .are-rate-top { display: flex; align-items: center; gap: 13px; margin-bottom: 18px; }
  .are-rate-av {
    width: 46px; height: 46px; border-radius: 50%; background: #1D1D1F; color: #fff;
    display: flex; align-items: center; justify-content: center; font-size: 15px; font-weight: 700; flex: none;
  }
  .are-rate-q { font-size: 18px; font-weight: 600; letter-spacing: -.01em; }
  .are-rate-who { font-size: 13px; color: #86868B; margin-top: 2px; }
  .are-rate-row { display: flex; gap: 10px; margin-bottom: 14px; flex-wrap: wrap; }
  .are-rate-pill {
    display: inline-flex; align-items: center; gap: 8px; border-radius: 980px; padding: 12px 24px;
    font-size: 15px; font-weight: 600; transition: all .2s var(--ft-ease);
    border: 1.5px solid var(--ts-paper-edge); background: #fff; color: #6E6E73; font-family: inherit; cursor: pointer;
  }
  .are-rate-pill:hover { transform: translateY(-1px); }
  .are-rate-pill:active { transform: scale(.96); }
  .are-rate-pill.no:hover { border-color: #86868B; color: #1D1D1F; }
  .are-rate-pill.selected.yes { border-color: var(--ft-green); color: var(--ft-green-ink); background: var(--ft-green-soft); }
  .are-rate-pill.selected.no { border-color: #1D1D1F; color: #1D1D1F; background: #F5F5F7; }
  .are-rate-note {
    width: 100%; background: #fff; border: 1px solid var(--ts-paper-edge); border-radius: 12px;
    padding: 13px 15px; font-size: 14px; color: #1D1D1F; outline: none; resize: none; font-family: inherit; box-sizing: border-box;
  }
  .are-rate-note::placeholder { color: #86868B; }
  .are-rate-submit {
    margin-top: 10px; padding: 9px 20px; border-radius: 980px; border: none; background: #1D1D1F; color: #fff;
    font-size: 13px; font-weight: 600; cursor: pointer; font-family: inherit; transition: opacity .15s;
  }
  .are-rate-submit:hover { opacity: .85; }
  .are-rate-cap { font-size: 12px; color: #86868B; margin-top: 11px; }

  /* ── Bottom CTAs ── */
  .are-endcta { display: flex; align-items: center; gap: 14px; justify-content: center; padding-top: 8px; flex-wrap: wrap; }
  .are-btn-again {
    display: inline-flex; align-items: center; gap: 10px; background: #1D1D1F; color: #fff;
    padding: 17px 38px; border-radius: 980px; font-size: 16px; font-weight: 600; letter-spacing: -.01em;
    border: none; cursor: pointer; font-family: inherit; transition: all .25s var(--ft-ease);
  }
  .are-btn-again:hover { transform: translateY(-2px); opacity: .9; }
  .are-btn-again:active { transform: scale(.97); opacity: .9; }
  .are-btn-done {
    display: inline-flex; align-items: center; background: transparent; color: #6E6E73;
    padding: 17px 26px; border-radius: 980px; font-size: 15px; font-weight: 500;
    border: none; cursor: pointer; font-family: inherit;
  }
  .are-btn-done:hover { background: #F5F5F7; color: #1D1D1F; }

  /* ELO delta chip — the number moved */
  .are-delta {
    display: inline-flex; align-items: center; gap: 7px; margin-top: 16px;
    font-family: var(--ts-mono); font-size: 13px; font-weight: 700;
    padding: 7px 14px; border-radius: 980px;
    animation: are-countpop .6s var(--ft-ease) .4s both;
  }
  .are-delta.up { background: var(--ft-green-soft); color: var(--ft-green-ink); }
  .are-delta.down { background: #F5F5F7; color: #6E6E73; }
  .are-delta .k { font-family: inherit; font-weight: 600; opacity: .75; }

  .are-btn-taco {
    display: inline-flex; align-items: center; gap: 10px; background: var(--ft-brand); color: #fff;
    padding: 17px 32px; border-radius: 980px; font-size: 16px; font-weight: 600; letter-spacing: -.01em;
    border: none; cursor: pointer; font-family: inherit; transition: all .25s var(--ft-ease);
  }
  .are-btn-taco:hover { transform: translateY(-2px); opacity: .92; }
  .are-btn-taco:active { transform: scale(.97); opacity: .92; }

  @media (max-width: 600px) {
    .are-headline { font-size: 42px; }
    .are-hero-inner { padding: 36px 16px 32px; }
    .are-content { padding: 24px 16px 60px; }
    .are-endcta { flex-direction: column; }
    .are-btn-again, .are-btn-done, .are-btn-taco { width: 100%; justify-content: center; }
    /* receipt stacks vertically — the FOR divider goes horizontal */
    .are-ticket-grid { flex-direction: column; }
    .are-tdiv { width: auto; height: 34px; border: none; border-top: 1px dashed var(--ts-paper-edge); border-bottom: 1px dashed var(--ts-paper-edge); flex-direction: row; }
    .are-tdiv .for { writing-mode: horizontal-tb; transform: none; }
    .are-servedstamp { top: 64px; right: 14px; font-size: 14px; }
  }

  @media (prefers-reduced-motion: reduce) {
    .are-page *, .are-page *::before, .are-page *::after {
      animation-duration: 0.01ms !important; animation-iteration-count: 1 !important; transition-duration: 0.01ms !important;
    }
  }
`;

function initials(name) {
  return (name || '?').charAt(0).toUpperCase();
}

function ordinal(n) {
  if (!n) return '';
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function assetLabel(a) {
  if (!a) return '?';
  if (a.type === 'pick') return `${a.season || ''} ${ordinal(a.round)}`.trim();
  return a.name || a.sleeperId || 'Player';
}

function AssetAvatar({ asset }) {
  const [err, setErr] = useState(false);
  if (asset?.type === 'pick') {
    return (
      <div className="are-pick-av">
        <span>'{String(asset.season || '').slice(-2)}</span>
        <span>{ordinal(asset.round)}</span>
      </div>
    );
  }
  const src = asset?.sleeperId && !err ? `${SLEEPERCDN}${asset.sleeperId}.jpg` : null;
  return (
    <div className="are-av">
      {src ? <img src={src} alt="" onError={() => setErr(true)} /> : initials(asset?.name)}
    </div>
  );
}

function AssetItem({ asset }) {
  const meta = asset?.type === 'pick'
    ? (asset.via ? `via ${asset.via}` : 'draft pick')
    : [asset?.position, asset?.team].filter(Boolean).join(' · ');
  return (
    <div className="are-titem">
      <AssetAvatar asset={asset} />
      <div>
        <div className="are-ti-name">{assetLabel(asset)}</div>
        {meta && <div className="are-ti-meta">{meta}</div>}
      </div>
    </div>
  );
}

export default function TradeArenaEnd() {
  const { sessionId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const data = location.state || {};
  const { status = 'deal_reached', dealOffer, eloResult, analystVerdict, abandonedBy } = data;

  const [rating, setRating] = useState(null);
  const [note, setNote] = useState('');
  const [ratingSaved, setRatingSaved] = useState(false);

  const myUsername = user?.sleeperUsername || user?.email?.split('@')[0] || 'You';
  const opponentUsername = data.opponentUsername || 'Opponent';
  const opponentIsBot = !!data.opponentIsBot;

  const isAbandoned = status === 'abandoned';
  const iAbandoned = isAbandoned && abandonedBy === user?.id;
  const isDeal = status === 'deal_reached';
  const timedOut = status === 'no_deal' && data.reason === 'time';

  // Which side am I? eloResult carries both players' numbers.
  const iAmP1 = data.player1UserId ? data.player1UserId === user?.id : true;
  const newElo = (iAmP1 ? eloResult?.newP1 : eloResult?.newP2) ?? null;
  const myEloDelta = (iAmP1 ? eloResult?.delta1 : eloResult?.delta2) ?? null;

  // Every deal cooked this session (multi-deal), oldest first. Falls back to the
  // legacy single-deal payload shape. Sides flip per deal: offering/asking are
  // from the SENDER's view.
  const deals = (data.deals && data.deals.length ? data.deals : (dealOffer ? [{ ...dealOffer, verdict: analystVerdict }] : []))
    .map((d) => {
      const iSent = d.senderUserId === user?.id;
      return {
        id: d.id,
        got: (iSent ? d.askingAssets : d.offeringAssets) || [],
        gave: (iSent ? d.offeringAssets : d.askingAssets) || [],
      };
    });
  const dealCount = data.dealCount ?? deals.length;

  // Per-player progression for this session
  const myProg = (data.progression || {})[user?.id] || null;
  const after = myProg?.after || {};
  const newlyUnlockedRating = !!myProg?.newlyUnlockedRating;
  const newBadges = myProg?.newBadges || [];

  const currentStreak = after.currentStreak ?? 0;
  const punchcardSize = after.punchcardSize ?? 8;
  const punchcardFilled = after.punchcardFilled ?? 0;
  const ratingUnlocked = after.ratingUnlocked ?? false;
  const dealsToUnlock = after.dealsToUnlock ?? 0;
  const punchcardFull = !!after.punchcardFilled && after.punchcardFilled >= punchcardSize;

  // The badge to celebrate in the unlock reveal
  const freshBadge = newBadges[0] || after.nextBadge || null;
  const freshBadgeLabel = freshBadge?.label || 'Repeat Customer';
  const freshBadgeEmoji = freshBadge?.emoji || '🏅';

  // Numbers climb on mount instead of just appearing — makes the win feel earned.
  const streakCount = useCountUp(currentStreak, 700);
  const eloCount = useCountUp(newElo, 900);
  const eloDeltaCount = useCountUp(myEloDelta != null ? Math.abs(myEloDelta) : null, 600);

  const handleRate = async () => {
    if (!rating || ratingSaved) return;
    try {
      await fetch(`/api/arena/session/${sessionId}/rating`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating, note }),
      });
      setRatingSaved(true);
    } catch (e) {}
  };

  // ── Hero copy by status ──
  let awningClass = 'green';
  let eyebrowClass = '';
  let eyebrowText = dealCount > 1 ? `${dealCount} deals cooked` : "Deal's cooked";
  let headline = <>Order up<span className="quiet">.</span></>;
  let dek = (
    <>You and <b>{opponentUsername}</b> cooked {dealCount > 1 ? <b>{dealCount} deals</b> : 'a deal'}. Receipts below — nothing's binding in your real league.</>
  );
  let heroMuted = false;

  if (isAbandoned && iAbandoned) {
    awningClass = 'muted';
    eyebrowClass = 'muted';
    eyebrowText = 'Walked away';
    headline = <>You left the line<span className="quiet">.</span></>;
    dek = <>You stepped off before the order closed. Your standing took a small hit.</>;
    heroMuted = true;
  } else if (isAbandoned && !iAbandoned) {
    eyebrowText = 'They walked';
    headline = <>{opponentUsername} bailed<span className="quiet">.</span></>;
    dek = <>{opponentUsername} went quiet at the window. No harm to you — the line stays open.</>;
  } else if (timedOut) {
    awningClass = 'muted';
    eyebrowClass = 'muted';
    eyebrowText = 'Kitchen closed';
    headline = <>Time's up<span className="quiet">. No deal.</span></>;
    dek = <>The clock beat you and {opponentUsername} to the plate. Two minutes goes fast — step back up and cook quicker.</>;
    heroMuted = true;
  } else if (!isDeal) {
    awningClass = 'muted';
    eyebrowClass = 'muted';
    eyebrowText = 'No deal';
    headline = <>No deal<span className="quiet">. Next time.</span></>;
    dek = <>You and {opponentUsername} couldn't find the plate. The window's still open whenever you are.</>;
    heroMuted = true;
  }

  const showRecap = isDeal && deals.length > 0;
  const showProgression = isDeal && myProg;
  const showRating = !iAbandoned;

  // Short order number from sessionId
  const orderNum = sessionId ? `#${String(sessionId).slice(-4)}` : '#0000';

  return (
    <>
      <style>{styles}</style>
      <div className="are-page">

        {/* ── Hero: order up ── */}
        <div className={`are-awning ${awningClass}`}></div>
        <section className={`are-hero${heroMuted ? ' muted' : ''}`}>
          <div className="are-hero-inner">
            <div className={`are-eyebrow${eyebrowClass ? ' ' + eyebrowClass : ''}`}>
              <span className="line"></span> {eyebrowText}
            </div>
            <h1 className="are-headline">{headline}</h1>
            <p className="are-dek">{dek}</p>
            {ratingUnlocked && !newlyUnlockedRating && myEloDelta != null && myEloDelta !== 0 && (
              <div className={`are-delta ${myEloDelta > 0 ? 'up' : 'down'}`}>
                <span className="k">STAND RATING</span> {myEloDelta > 0 ? '+' : '-'}{eloDeltaCount} → {newElo != null ? eloCount.toLocaleString() : '—'}
              </div>
            )}
          </div>
        </section>

        <div className="are-content">


          {/* ── CTAs first — play again without scrolling ── */}
          <div className="are-endcta" style={{ paddingTop: 0, marginBottom: 34 }}>
            {opponentIsBot && (
              <button className="are-btn-taco" onClick={() => navigate('/taco-stand', { state: { autoBot: true } })}>
                <span>🌮</span> Run it back with the Taco <span>→</span>
              </button>
            )}
            <button className="are-btn-again" onClick={() => navigate('/taco-stand')}>
              <span>🌮</span> Back to the window — find another <span>→</span>
            </button>
            <button className="are-btn-done" onClick={() => navigate('/taco-stand')}>
              Done for now
            </button>
          </div>


          {/* ── The deals — one clean receipt each ── */}
          {showRecap && (
            <section className="are-section">
              <div className="are-sectionhead">
                <span className="are-klabel">{deals.length > 1 ? `Your ${deals.length} deals` : 'The deal'}</span>
              </div>
              {deals.map((deal, idx) => (
                <div className="are-receiptwrap" key={deal.id || idx} style={idx > 0 ? { marginTop: 14 } : undefined}>
                  <div className="are-receipt">
                    <span className="are-servedstamp">SERVED</span>
                    <div className="are-receipt-top">
                      <span className="are-receipt-num">{deals.length > 1 ? <>DEAL <b>{idx + 1}</b> of {deals.length}</> : <>ORDER · <b>{orderNum}</b></>} · {myUsername} ⇄ {opponentUsername}</span>
                    </div>
                    <div className="are-ticket-grid">
                      <div className="are-tcol get">
                        <div className="are-tcol-k">🟢 You got</div>
                        {deal.got.length > 0
                          ? deal.got.map((a, i) => <AssetItem key={i} asset={a} />)
                          : <div className="are-empty">Nothing on this side</div>}
                      </div>
                      <div className="are-tdiv"><span className="for">— FOR —</span></div>
                      <div className="are-tcol give">
                        <div className="are-tcol-k">You gave</div>
                        {deal.gave.length > 0
                          ? deal.gave.map((a, i) => <AssetItem key={i} asset={a} />)
                          : <div className="are-empty">Nothing on this side</div>}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </section>
          )}

          {/* ── THE PEAK: streak fills + rating unlocks ── */}
          {showProgression && (
            <section className="are-section">
              <div className="are-sectionhead">
                <span className={`are-opensign${punchcardFull ? ' brand' : ''}`}>
                  <span className="pip"></span> {punchcardFull ? 'Card complete' : 'On the card'}
                </span>
                <span className="are-klabel" style={{ marginLeft: 'auto' }}>streak → rating</span>
              </div>
              <div className="are-peak">

                {/* Punch card */}
                <div className="are-punch">
                  {punchcardFull && <Confetti />}
                  <div className="are-punch-top">
                    <div>
                      <div className="are-punch-title">{punchcardFull ? 'Punch card · full' : 'Punch card'}</div>
                      <div className="are-punch-sub">
                        {punchcardFull
                          ? "This deal punched your last stamp. The card's done."
                          : 'This deal punched another stamp. Keep the streak alive.'}
                      </div>
                    </div>
                    <div className="are-streakwrap">
                      <div className="are-streaknum"><span className="flame">🔥</span>{streakCount}</div>
                      <div className="are-streaklbl">deal streak</div>
                    </div>
                  </div>
                  <div className="are-stamps">
                    {Array.from({ length: punchcardSize }).map((_, i) => {
                      const filled = i < punchcardFilled;
                      const isFresh = filled && i === punchcardFilled - 1;
                      let cls = 'are-stamp';
                      if (isFresh) cls += ' fresh';
                      else if (filled) cls += ' done';
                      return <div key={i} className={cls}>🌮</div>;
                    })}
                    <div className={`are-stamp goal${punchcardFull ? ' hit' : ''}`}>★</div>
                  </div>
                  <div className="are-punch-foot">
                    {punchcardFull
                      ? <>
                          <span className="are-cardfull">✓ Card full</span>
                          <span>{punchcardSize}-deal streak hit — you earned <b>{freshBadgeLabel}</b>.</span>
                        </>
                      : <span>{Math.max(punchcardSize - punchcardFilled, 0)} more to fill the card.</span>}
                  </div>
                </div>

                {/* Reward side — unlock reveal, locked progress, or plain rating */}
                {newlyUnlockedRating ? (
                  <div className="are-unlock">
                    <Confetti />
                    <div className="awn"></div>
                    <div className="shine"></div>
                    <div className="are-unlock-tag"><span className="new">New</span> Stand Rating unlocked</div>
                    <div className="are-unlock-badge">
                      <div className="are-ub-medal">{freshBadgeEmoji}</div>
                      <div className="are-ub-txt">
                        <div className="ub-k">Badge earned</div>
                        <div className="ub-name">{freshBadgeLabel}</div>
                      </div>
                    </div>
                    <div className="are-ratingreveal">
                      <div className="are-rr-k">Your Stand Rating <span className="wasloc">was 🔒 locked</span></div>
                      <div className="are-rr-num">
                        {newElo != null ? eloCount.toLocaleString() : '—'}
                        {after.rank && <span className="rank">{after.rank}</span>}
                      </div>
                      <div className="are-rr-sub">
                        It was hidden until you filled the card. From here, every deal you cook <b>moves the number</b>.
                      </div>
                    </div>
                  </div>
                ) : !ratingUnlocked ? (
                  <div className="are-locknote">
                    <div className="are-lock-medal">🔒</div>
                    <div className="are-lock-txt">
                      <div className="lk"><b>{Math.max(dealsToUnlock, 0)} more {dealsToUnlock === 1 ? 'deal' : 'deals'}</b> till Repeat Customer</div>
                      <div className="lk-sub">Fill the punch card to unlock your Stand Rating.</div>
                    </div>
                  </div>
                ) : (
                  <div className="are-rating-plain">
                    <div>
                      <div className="rp-k">Stand Rating</div>
                      {after.rank && <div className="rp-rank">{after.rank}</div>}
                    </div>
                    <div className="rp-num">{newElo != null ? eloCount.toLocaleString() : '—'}</div>
                  </div>
                )}

              </div>
            </section>
          )}

          {/* ── Rate your trade partner ── */}
          {showRating && (
            <section className="are-section">
              <div className="are-sectionhead">
                <span className="are-klabel">Rate your trade partner</span>
              </div>
              <div className="are-ratecard">
                <div className="are-rate-top">
                  <div className="are-rate-av">{initials(opponentUsername)}</div>
                  <div>
                    <div className="are-rate-q">Would you deal with {opponentUsername} again?</div>
                    <div className="are-rate-who">@{opponentUsername}</div>
                  </div>
                </div>
                {ratingSaved ? (
                  <div className="are-rate-cap">Rating saved. Thank you.</div>
                ) : (
                  <>
                    <div className="are-rate-row">
                      <button
                        className={`are-rate-pill yes${rating === 'up' ? ' selected' : ''}`}
                        onClick={() => setRating('up')}
                      >👍 Yes — solid</button>
                      <button
                        className={`are-rate-pill no${rating === 'down' ? ' selected' : ''}`}
                        onClick={() => setRating('down')}
                      >👎 No — pass next time</button>
                    </div>
                    {rating && (
                      <>
                        <textarea
                          className="are-rate-note"
                          rows={2}
                          placeholder="Optional — a quick word on how they haggled."
                          value={note}
                          onChange={e => setNote(e.target.value)}
                        />
                        <button className="are-rate-submit" onClick={handleRate}>Submit</button>
                      </>
                    )}
                    <div className="are-rate-cap">This shapes their rating, not yours.</div>
                  </>
                )}
              </div>
            </section>
          )}

        </div>
      </div>
    </>
  );
}
